import { UntypedFormGroup } from '@angular/forms';
import { PerfilSeccionId } from '../../data/perfil-microempresario.content';
import {
  PerfilDocumentoModalConfig,
  PerfilDocumentoModalTip,
} from '../shared/perfil-documento-modal/perfil-documento-modal.model';
import {
  PerfilDocumentoDef,
  UploadedTipoDocumento,
} from '../progress/perfil-progress.definitions';
import { PerfilSegmento } from '../segmento/perfil-segmento.util';

/** Tipo documento anidado en grupo (NeoTipoDocumentoDTO). */
export interface NeoTipoDocumentoPerfilApi {
  idTipoDocumento?: number;
  nombre?: string;
  tipoDocumento?: string;
  obligatorio?: boolean | number;
  foto?: number | boolean;
  multiple?: boolean | number;
  cantidadArchivos?: number;
  extensionesValidas?: string;
  tipoArchivoPermitido?: string;
  estado?: string;
  orden?: number;
  seccionUbicacion?: string | null;
  nombreArchivoGuardado?: string;
  idGrupoTipoDocumento?: number;
}

/** Grupo (NeoGrupoTipoDocumentoDTO) — texto e imagen del modal. */
export interface NeoGrupoTipoDocumentoPerfilApi {
  idGrupoTipoDocumento?: number;
  nombre?: string;
  descripcion?: string;
  guiaUsuario?: string;
  estado?: string;
  idArchivoPublico?: number | null;
  neoTipoDocumentoDTOS?: NeoTipoDocumentoPerfilApi[];
}

/** Respuesta NeoResponseDTO de tiposDocumentos/perfil/{tipo}. */
export interface NeoGruposPerfilResponse {
  exitoso?: boolean;
  data?: NeoGrupoTipoDocumentoPerfilApi[];
}

const SECCION_API_TO_PERFIL: Record<string, PerfilSeccionId> = {
  SOBRE_TI: 'sobre-ti',
  SOBRE_NEGOCIO: 'sobre-negocio',
  SOBRE_INGRESOS: 'sobre-ingresos',
};

/** Pasos UI donde se inyectan docs (no nombres de documentos). */
export const PERFIL_DOC_PASO = {
  documentos: 'documentos',
  fotos: 'fotos',
  infoAdicional: 'info-adicional',
} as const;

export type PerfilDocPasoId =
  (typeof PERFIL_DOC_PASO)[keyof typeof PERFIL_DOC_PASO];

const MODAL_TITLE_DEFAULT = 'Ten en cuenta...';
const MODAL_CTA_DEFAULT = 'Subir documento';

/** PAR tamaño máx. docs (MB) — misma regla que files.form. */
export const PAR_TAMANO_MAX_ARCHIVOS = 'PAR-122';
/** PAR tamaño máx. multimedia / video (MB). */
export const PAR_TAMANO_MAX_MULTIMEDIA = 'PAR-056';

/**
 * Clave estable en form/progress: solo id de tipo documento.
 * No depende del nombre (puede cambiar por ambiente / admin).
 */
export function valueKeyFromTipoId(
  idTipoDocumento: number | string | null | undefined
): string {
  return `doc_${idTipoDocumento}`;
}

/** Declaración de renta (mismo criterio que DOC_RENTA en back). */
export function isDeclaracionRentaDoc(
  doc: Pick<
    PerfilDocumentoDef,
    'label' | 'nombreArchivoGuardado' | 'codigo'
  >
): boolean {
  const texto = `${doc.label ?? ''} ${doc.nombreArchivoGuardado ?? ''} ${
    doc.codigo ?? ''
  }`.toLowerCase();
  return texto.includes('renta');
}

/** Quita controles `doc_*` que ya no están en el catálogo visible de la sección. */
/** Etiqueta de peso para el chip de archivo cargado. */
export function formatBytesLabel(
  bytes: number | string | null | undefined
): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) {
    return '—';
  }
  if (n < 1024) {
    return `${Math.round(n)} B`;
  }
  const kb = n / 1024;
  if (kb < 1024) {
    return `${Math.round(kb)} kb`;
  }
  return `${(kb / 1024).toFixed(1)} MB`;
}

export function pruneStaleDocControls(
  form: UntypedFormGroup,
  docsFiles: Record<string, unknown>,
  keepDocs: ReadonlyArray<Pick<PerfilDocumentoDef, 'valueKey'>>
): void {
  const keep = new Set(keepDocs.map((d) => d.valueKey));
  for (const key of Object.keys(form.controls)) {
    if (!key.startsWith('doc_') || keep.has(key)) {
      continue;
    }
    form.removeControl(key);
    delete docsFiles[key];
  }
}

export function isMultimediaDoc(doc: Pick<PerfilDocumentoDef, 'esFoto' | 'extensionesValidas' | 'tipoArchivoPermitido' | 'label'>): boolean {
  if (doc.esFoto) {
    return true;
  }
  const blob = `${doc.extensionesValidas ?? ''} ${doc.tipoArchivoPermitido ?? ''} ${doc.label ?? ''}`.toLowerCase();
  return /mp4|mov|webm|video/.test(blob);
}

/** Título de slot: agrega "(opcional)" si no es obligatorio. */
export function labelConOpcional(
  label: string,
  obligatorio: boolean
): string {
  const base = (label ?? '').trim();
  if (!base) {
    return obligatorio ? '' : '(opcional)';
  }
  if (obligatorio || /\(opcional\)\s*$/i.test(base)) {
    return base;
  }
  return `${base} (opcional)`;
}

export function segmentoToTipoPersonaApi(
  segmento: PerfilSegmento
): 'express' | 'natural' {
  return segmento === 'express' ? 'express' : 'natural';
}


export function mapSeccionApiToPerfil(
  codigo: string | null | undefined,
  opts: { juridica?: boolean; esFoto?: boolean } = {}
): PerfilSeccionId | null {
  const raw = String(codigo ?? '')
    .trim()
    .toUpperCase();
  if (!raw) {
    // Sin parametrización: fallback histórico (PJ empresa+tú / PN negocio).
    if (opts.esFoto) {
      return opts.juridica ? 'sobre-ti' : 'sobre-negocio';
    }
    return null;
  }
  // PJ unifica SOBRE_NEGOCIO y SOBRE_TI en la sección “Sobre la empresa y tú”.
  // SOBRE_INGRESOS (y el resto) se respetan tal cual, incluidas las fotos.
  if (opts.juridica && (raw === 'SOBRE_NEGOCIO' || raw === 'SOBRE_TI')) {
    return 'sobre-ti';
  }
  return SECCION_API_TO_PERFIL[raw] ?? null;
}

export function isFotoNegocio(doc: NeoTipoDocumentoPerfilApi): boolean {
  return Number(doc.foto) === 1 || doc.foto === true;
}

export function isTipoDocumentoHabilitado(
  estado: string | null | undefined
): boolean {
  return String(estado ?? '').trim().toUpperCase() === 'HABILITADO';
}

export function isObligatorioDoc(doc: NeoTipoDocumentoPerfilApi): boolean {
  return doc.obligatorio === true || doc.obligatorio === 1;
}

export function isMultipleDoc(
  doc: Pick<PerfilDocumentoDef, 'multiple'> | NeoTipoDocumentoPerfilApi
): boolean {
  return doc.multiple === true || doc.multiple === 1;
}

/** Slots UI: 1 si no es multiple; si multiple, max(1, cantidadArchivos). */
export function cantidadSlotsDoc(
  doc: Pick<PerfilDocumentoDef, 'multiple' | 'cantidadArchivos'>
): number {
  if (!isMultipleDoc(doc)) {
    return 1;
  }
  const n = Number(doc.cantidadArchivos);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
}

const SLOT_SEP = '__slot_';

export function slotKey(valueKey: string, index: number): string {
  return `${valueKey}${SLOT_SEP}${index}`;
}

export function baseValueKeyFromSlot(slotOrValueKey: string): string {
  const i = slotOrValueKey.indexOf(SLOT_SEP);
  return i >= 0 ? slotOrValueKey.slice(0, i) : slotOrValueKey;
}

export function slotIndexFromKey(slotOrValueKey: string): number {
  const i = slotOrValueKey.indexOf(SLOT_SEP);
  if (i < 0) {
    return 0;
  }
  const n = Number(slotOrValueKey.slice(i + SLOT_SEP.length));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export interface PerfilDocSlot {
  index: number;
  slotKey: string;
}

export function slotsForDoc(
  doc: Pick<PerfilDocumentoDef, 'valueKey' | 'multiple' | 'cantidadArchivos'>
): PerfilDocSlot[] {
  if (!isMultipleDoc(doc)) {
    return [{ index: 0, slotKey: doc.valueKey }];
  }
  const n = cantidadSlotsDoc(doc);
  return Array.from({ length: n }, (_, index) => ({
    index,
    slotKey: slotKey(doc.valueKey, index),
  }));
}


export function resolvePasoId(
  seccionId: PerfilSeccionId,
  opts: {
    esFoto: boolean;
    obligatorio: boolean;
    keepInDocumentos?: boolean;
    juridica?: boolean;
  }
): PerfilDocPasoId {
  if (opts.esFoto) {
    return PERFIL_DOC_PASO.fotos;
  }
  if (opts.keepInDocumentos) {
    return PERFIL_DOC_PASO.documentos;
  }
  // Opcionales de negocio/ingresos van al paso “info adicional”.
  if (
    !opts.obligatorio &&
    (seccionId === 'sobre-negocio' || seccionId === 'sobre-ingresos')
  ) {
    return PERFIL_DOC_PASO.infoAdicional;
  }
  if (!opts.obligatorio && seccionId === 'sobre-ti' && opts.juridica) {
    return PERFIL_DOC_PASO.infoAdicional;
  }
  return PERFIL_DOC_PASO.documentos;
}

/** Slots v2 = catálogo (con sección) ∩ tipos que devolvió el servicio de solicitud. */
export function intersectSeccionDocsConSolicitud(
  catalogDocs: ReadonlyArray<PerfilDocumentoDef>,
  uploaded: {
    archivos?: UploadedTipoDocumento[];
    fotos?: UploadedTipoDocumento[];
  },
  seccionId: PerfilSeccionId,
  opts: { juridica?: boolean } = {}
): PerfilDocumentoDef[] {
  const items = [...(uploaded.archivos ?? []), ...(uploaded.fotos ?? [])];
  if (!items.length) {
    return catalogDocs
      .filter((doc) => doc.seccionId === seccionId)
      .slice()
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
  }
  const byId = new Map<number, UploadedTipoDocumento>();
  for (const item of items) {
    const id = Number(item.idTipoDocumento);
    if (Number.isFinite(id)) {
      byId.set(id, item);
    }
  }
  return catalogDocs
    .filter((doc) => {
      const id = Number(doc.idTipoDocumento ?? doc.id);
      if (!byId.has(id)) {
        return false;
      }
      return doc.seccionId === seccionId;
    })
    .map((doc) => {
      const sol = byId.get(Number(doc.idTipoDocumento ?? doc.id));
      const resolvedSeccion = doc.seccionId;
      if (sol?.obligatorio == null) {
        return {
          ...doc,
          seccionId: resolvedSeccion,
          pasoId: resolvePasoId(resolvedSeccion, {
            esFoto: !!doc.esFoto,
            obligatorio: !!doc.obligatorio,
            // PN: renta se queda en el paso de docs (pregunta/PAR).
            // PJ: sigue solo el flag (PAR-117) → opcionales si no aplica.
            keepInDocumentos:
              !opts.juridica && isDeclaracionRentaDoc(doc),
            juridica: opts.juridica,
          }),
        };
      }
      const obligatorio = Number(sol.obligatorio) === 1 || sol.obligatorio === true;
      return {
        ...doc,
        seccionId: resolvedSeccion,
        obligatorio,
        pasoId: resolvePasoId(resolvedSeccion, {
          esFoto: !!doc.esFoto,
          obligatorio,
          keepInDocumentos:
            !opts.juridica && isDeclaracionRentaDoc(doc),
          juridica: opts.juridica,
        }),
      };
    })
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
}

/**
 * Completa defs de fotos/docs empresa PJ desde la respuesta de solicitud
 * cuando el catálogo perfil no las trae (sin seccionUbicacion / tipología incompleta).
 */
export function mergeSolicitudTiposEnSeccion(
  current: ReadonlyArray<PerfilDocumentoDef>,
  uploaded: {
    archivos?: UploadedTipoDocumento[];
    fotos?: UploadedTipoDocumento[];
  },
  seccionId: PerfilSeccionId,
  opts: { juridica?: boolean } = {}
): PerfilDocumentoDef[] {
  const result = current.slice();
  const seen = new Set(
    result.map((d) => Number(d.idTipoDocumento ?? d.id)).filter(Number.isFinite)
  );
  const pushFrom = (
    items: ReadonlyArray<UploadedTipoDocumento> | undefined,
    asFoto: boolean
  ) => {
    for (const item of items ?? []) {
      const id = Number(item.idTipoDocumento);
      if (!Number.isFinite(id) || seen.has(id)) {
        continue;
      }
      const esFoto = asFoto || isFotoNegocio(item);
      if (!isTipoDocumentoHabilitado(item.estado)) {
        continue;
      }
      if (opts.juridica && seccionId === 'sobre-ti') {
        // Respetar seccionUbicacion también para fotos (igual que docs).
        if (item.seccionUbicacion) {
          const mapped = mapSeccionApiToPerfil(item.seccionUbicacion, {
            juridica: true,
            esFoto,
          });
          if (mapped !== 'sobre-ti') {
            continue;
          }
        } else if (!esFoto) {
          continue;
        }
      }
      seen.add(id);
      const obligatorio =
        item.obligatorio == null
          ? esFoto
          : Number(item.obligatorio) === 1 || item.obligatorio === true;
      const label = String(item.nombre || `Documento ${id}`);
      result.push({
        id: String(id),
        codigo: String(item.nombreArchivoGuardado || id),
        label,
        seccionId,
        obligatorio,
        valueKey: valueKeyFromTipoId(id),
        pasoId: resolvePasoId(seccionId, {
          esFoto,
          obligatorio,
          juridica: opts.juridica,
        }),
        idTipoDocumento: id,
        esFoto,
        orden: item.orden,
        extensionesValidas: item.extensionesValidas,
        tipoArchivoPermitido: item.tipoArchivoPermitido,
        nombreArchivoGuardado: item.nombreArchivoGuardado,
      });
    }
  };
  pushFrom(uploaded.fotos, true);
  pushFrom(uploaded.archivos, false);
  return result.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
}

/**
 * Aplana grupos API → defs UI/progress.
 * Solo docs con seccionUbicacion en BD. valueKey = doc_{idTipoDocumento}.
 */
export function mapGruposApiToPerfilDocumentos(
  grupos: ReadonlyArray<NeoGrupoTipoDocumentoPerfilApi>,
  opts: { juridica?: boolean } = {}
): PerfilDocumentoDef[] {
  const result: PerfilDocumentoDef[] = [];

  for (const grupo of grupos) {
    const tipos = grupo.neoTipoDocumentoDTOS ?? [];
    for (const doc of tipos) {
      const estado = String(doc.estado ?? '').trim().toUpperCase();
      if (!isTipoDocumentoHabilitado(estado)) {
        continue;
      }
      const esFoto = isFotoNegocio(doc);
      const seccionId = mapSeccionApiToPerfil(doc.seccionUbicacion, {
        juridica: opts.juridica,
        esFoto,
      });
      if (!seccionId || doc.idTipoDocumento == null) {
        continue;
      }

      const valueKey = valueKeyFromTipoId(doc.idTipoDocumento);
      const obligatorio = isObligatorioDoc(doc);
      const multiple = isMultipleDoc(doc);
      const cantidadArchivos = multiple
        ? Math.max(1, Number(doc.cantidadArchivos) || 1)
        : 1;
      const id = String(doc.idTipoDocumento);
      const label = doc.nombre ?? grupo.nombre ?? `Documento ${id}`;
      const codigo = doc.nombreArchivoGuardado || id;
      const keepInDocumentos =
        !opts.juridica &&
        isDeclaracionRentaDoc({
          label,
          nombreArchivoGuardado: doc.nombreArchivoGuardado,
          codigo,
        });
      result.push({
        id,
        codigo,
        label,
        seccionId,
        obligatorio,
        valueKey,
        pasoId: resolvePasoId(seccionId, {
          esFoto,
          obligatorio,
          keepInDocumentos,
          juridica: opts.juridica,
        }),
        grupoId: grupo.idGrupoTipoDocumento,
        descripcion: grupo.descripcion ?? undefined,
        guiaUsuario: grupo.guiaUsuario ?? undefined,
        idArchivoPublico: grupo.idArchivoPublico ?? null,
        extensionesValidas: doc.extensionesValidas ?? undefined,
        idTipoDocumento: doc.idTipoDocumento,
        tipoArchivoPermitido: doc.tipoArchivoPermitido,
        nombreArchivoGuardado: doc.nombreArchivoGuardado,
        esFoto,
        orden: doc.orden,
        multiple,
        cantidadArchivos,
      });
    }
  }

  return result.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
}

/** Accept HTML del input file a partir de extensiones BD (ej. "jpg,png,pdf"). */
export function acceptFromExtensiones(
  extensiones: string | null | undefined
): string {
  const raw = (extensiones ?? '')
    .split(/[,;\s]+/)
    .map((e) => e.trim().toLowerCase().replace(/^\./, ''))
    .filter(Boolean);
  if (!raw.length) {
    return '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf';
  }
  const parts = new Set<string>();
  for (const ext of raw) {
    parts.add(`.${ext}`);
    if (ext === 'jpg' || ext === 'jpeg') {
      parts.add('.jpg');
      parts.add('.jpeg');
      parts.add('image/jpeg');
    } else if (ext === 'png') {
      parts.add('image/png');
    } else if (ext === 'pdf') {
      parts.add('application/pdf');
    } else if (ext === 'mp4' || ext === 'mov' || ext === 'webm') {
      parts.add('video/*');
    }
  }
  return [...parts].join(',');
}

/** Tips del modal desde guiaUsuario (líneas) + formatos + tamaño máx. */
export function tipsFromGrupo(
  guiaUsuario: string | null | undefined,
  descripcion: string | null | undefined,
  extensionesValidas: string | null | undefined,
  tamanoMaxMb?: number | null
): PerfilDocumentoModalTip[] {
  const fromGuia = splitTipLines(guiaUsuario);
  const fromDesc = splitTipLines(descripcion);
  const tips = (fromGuia.length ? fromGuia : fromDesc).map(lineToTip);

  const formats = formatLabelFromExtensiones(extensionesValidas);
  const alreadyHasFormats = tips.some(
    (t) =>
      /formato/i.test(t.text) ||
      (t.emph != null && /jpg|png|pdf|mp4/i.test(t.emph))
  );
  if (formats && !alreadyHasFormats) {
    tips.push({ text: 'Formatos permitidos: ', emph: formats });
  }

  const sizeLabel =
    tamanoMaxMb != null && Number.isFinite(tamanoMaxMb) && tamanoMaxMb > 0
      ? `${tamanoMaxMb} MB`
      : null;
  const alreadyHasSize = tips.some((t) => /tama[nñ]o\s*m[aá]ximo/i.test(t.text));
  if (sizeLabel && !alreadyHasSize) {
    tips.push({ text: 'Tamaño máximo: ', emph: sizeLabel });
  } else if (sizeLabel) {
    for (let i = 0; i < tips.length; i++) {
      if (/tama[nñ]o\s*m[aá]ximo/i.test(tips[i].text)) {
        tips[i] = { text: 'Tamaño máximo: ', emph: sizeLabel };
      }
    }
  }

  return tips;
}

function splitTipLines(raw: string | null | undefined): string[] {
  if (!raw) {
    return [];
  }
  return String(raw)
    .split(/\r?\n|;/)
    .map((l) => l.trim())
    .filter(Boolean);
}

function lineToTip(line: string): PerfilDocumentoModalTip {
  return { text: line };
}

function formatLabelFromExtensiones(
  extensionesValidas: string | null | undefined
): string | null {
  const parts = (extensionesValidas ?? '')
    .split(/[,;\s]+/)
    .map((e) => e.trim().toUpperCase().replace(/^\./, ''))
    .filter(Boolean);
  if (!parts.length) {
    return null;
  }
  return parts.join(', ');
}

export function buildDocumentoModalConfig(
  doc: PerfilDocumentoDef,
  ejemploUrl: string | null,
  opts: { tamanoMaxMb?: number | null } = {}
): PerfilDocumentoModalConfig {
  const samples =
    ejemploUrl != null && ejemploUrl !== ''
      ? [
          {
            id: 'ejemplo',
            images: [ejemploUrl] as const,
          },
        ]
      : [];

  return {
    title: MODAL_TITLE_DEFAULT,
    cta: MODAL_CTA_DEFAULT,
    tips: tipsFromGrupo(
      doc.guiaUsuario,
      doc.descripcion,
      doc.extensionesValidas,
      opts.tamanoMaxMb
    ),
    samples,
    sampleLayout: 'single',
  };
}

export function parseGruposPerfilResponse(
  raw: unknown
): NeoGrupoTipoDocumentoPerfilApi[] {
  if (Array.isArray(raw)) {
    return raw as NeoGrupoTipoDocumentoPerfilApi[];
  }
  if (
    raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as NeoGruposPerfilResponse).data)
  ) {
    return (raw as NeoGruposPerfilResponse).data!;
  }
  return [];
}
